/**
 * Normalize organization instructor bulk course update log payloads from the API.
 */

import { normalizeExampleUpdatePreview } from '@/utils/courseVersionUpdateExamplePreview.js'

function toTrimmedString (value) {
    if (value == null) return ''
    return String(value).trim()
}

export function normalizeOrganizationCourseId (id) {
    if (id == null || id === '') return ''
    return String(id).trim()
}

function toPercent (value) {
    if (value == null || value === '') return null
    const n = Number(value)
    if (!Number.isFinite(n)) return null
    return Math.max(0, Math.min(100, Math.round(n)))
}

function normalizeLearnerOutcome (raw = {}) {
    const userId = raw.userId ?? raw.user_id ?? raw.id ?? null
    const userName = toTrimmedString(
        raw.userName ?? raw.user_name ?? raw.name ?? raw.learnerName ?? raw.learner_name
    )
    const userEmail = toTrimmedString(raw.userEmail ?? raw.user_email ?? raw.email)
    const status = toTrimmedString(raw.status) || 'updated'
    return {
        userId: userId != null ? String(userId) : '',
        userName: userName || userEmail || 'Learner',
        userEmail,
        previousPercentComplete: toPercent(
            raw.previousPercentComplete
            ?? raw.previous_percent_complete
            ?? raw.percentBefore
            ?? raw.percent_before
            ?? raw.beforePercent
            ?? raw.before_percent
        ),
        newPercentComplete: toPercent(
            raw.newPercentComplete
            ?? raw.new_percent_complete
            ?? raw.percentAfter
            ?? raw.percent_after
            ?? raw.afterPercent
            ?? raw.after_percent
        ),
        previousCourseVersion: toTrimmedString(
            raw.previousCourseVersion ?? raw.previous_course_version ?? raw.fromVersion ?? raw.from_version
        ),
        newCourseVersion: toTrimmedString(
            raw.newCourseVersion ?? raw.new_course_version ?? raw.toVersion ?? raw.to_version
        ),
        status,
        errorMessage: toTrimmedString(raw.errorMessage ?? raw.error_message ?? raw.error),
    }
}

export function normalizeOrganizationCourseUpdateEvent (raw = {}) {
    const eventId = raw.eventId ?? raw.event_id ?? raw.id ?? raw._id ?? null
    const courseId = raw.courseId ?? raw.course_id ?? null
    const updatedByInstructorId = raw.updatedByInstructorId
        ?? raw.updated_by_instructor_id
        ?? raw.instructorId
        ?? raw.instructor_id
        ?? null
    const learnerOutcomesRaw = raw.learnerOutcomes
        ?? raw.learner_outcomes
        ?? raw.results
        ?? raw.learners
        ?? []
    const learnerOutcomes = Array.isArray(learnerOutcomesRaw)
        ? learnerOutcomesRaw.map(normalizeLearnerOutcome)
        : []

    const updatedCount = Number(
        raw.learnersUpdatedCount
        ?? raw.learners_updated_count
        ?? raw.updatedCount
        ?? raw.updated_count
    ) || learnerOutcomes.filter((l) => l.status === 'updated').length

    const skippedCount = Number(
        raw.learnersSkippedCount
        ?? raw.learners_skipped_count
        ?? raw.skippedAlreadyCurrentCount
        ?? raw.skipped_already_current_count
        ?? raw.skippedCount
        ?? raw.skipped_count
    ) || learnerOutcomes.filter((l) => l.status === 'skipped').length

    const errorCount = Number(
        raw.learnersErrorCount
        ?? raw.learners_error_count
        ?? raw.errorCount
        ?? raw.error_count
    ) || learnerOutcomes.filter((l) => l.status === 'error').length

    return {
        eventId: eventId != null ? String(eventId) : '',
        courseId: courseId != null ? String(courseId) : '',
        courseName: toTrimmedString(raw.courseName ?? raw.course_name ?? raw.name),
        updatedByInstructorId: updatedByInstructorId != null ? String(updatedByInstructorId) : '',
        updatedByInstructorName: toTrimmedString(
            raw.updatedByInstructorName
            ?? raw.updated_by_instructor_name
            ?? raw.instructorName
            ?? raw.instructor_name
        ) || 'An instructor',
        updatedByInstructorEmail: toTrimmedString(
            raw.updatedByInstructorEmail
            ?? raw.updated_by_instructor_email
            ?? raw.instructorEmail
            ?? raw.instructor_email
        ),
        updatedAt: raw.updatedAt ?? raw.updated_at ?? raw.completedAt ?? raw.completed_at ?? null,
        previousOrgAssignedVersion: toTrimmedString(
            raw.previousOrgAssignedVersion
            ?? raw.previous_org_assigned_version
            ?? raw.fromVersion
            ?? raw.from_version
            ?? raw.previousVersion
            ?? raw.previous_version
        ),
        newOrgAssignedVersion: toTrimmedString(
            raw.newOrgAssignedVersion
            ?? raw.new_org_assigned_version
            ?? raw.toVersion
            ?? raw.to_version
            ?? raw.newVersion
            ?? raw.new_version
            ?? raw.targetPublishedVersion
            ?? raw.target_published_version
        ),
        learnersUpdatedCount: updatedCount,
        learnersSkippedCount: skippedCount,
        learnersErrorCount: errorCount,
        learnerOutcomes,
        jobId: toTrimmedString(raw.jobId ?? raw.job_id),
        exampleUpdatePreview: normalizeExampleUpdatePreview(raw),
    }
}

/**
 * Resolve before/after curriculum versions for org update previews.
 * The org catalog course may already be on the newest version — use the event (not the live course) for "before".
 *
 * @param {Record<string, unknown> | null | undefined} event
 * @param {Record<string, unknown> | null | undefined} catalogCourse
 */
export function resolveOrgCourseUpdateEventVersions (event, catalogCourse = null) {
    const normalized = event && typeof event === 'object'
        ? normalizeOrganizationCourseUpdateEvent(event)
        : normalizeOrganizationCourseUpdateEvent({})

    let previous = normalized.previousOrgAssignedVersion
    let next = normalized.newOrgAssignedVersion

    const outcomes = Array.isArray(normalized.learnerOutcomes) ? normalized.learnerOutcomes : []
    const sampleLearner = outcomes.find((row) => row.status === 'updated') || outcomes[0]
    if (sampleLearner) {
        if (!previous) {
            previous = toTrimmedString(sampleLearner.previousCourseVersion)
        }
        if (!next) {
            next = toTrimmedString(sampleLearner.newCourseVersion)
        }
    }

    const catalog = catalogCourse && typeof catalogCourse === 'object' ? catalogCourse : null
    if (!next && catalog) {
        next = toTrimmedString(
            catalog.instructorCourseVersion
            ?? catalog.instructor_course_version
            ?? catalog.version
        )
    }

    return {
        previous: previous || '—',
        next: next || '—',
    }
}

export function normalizeOrganizationCourseUpdateEvents (payload) {
    const list = payload?.events
        ?? payload?.updateEvents
        ?? payload?.update_events
        ?? payload?.items
        ?? (Array.isArray(payload) ? payload : [])
    if (!Array.isArray(list)) return []
    return list.map(normalizeOrganizationCourseUpdateEvent).filter((e) => e.eventId)
}

export function getOrganizationCourseUpdateEventTimestamp (event) {
    if (!event) return 0
    const value = event.updatedAt ?? event.updated_at ?? event.completedAt ?? event.completed_at
    if (value == null || value === '') return 0
    const n = Number(value)
    const date = Number.isFinite(n) && n > 1e11 ? new Date(n) : new Date(value)
    const time = date.getTime()
    return Number.isNaN(time) ? 0 : time
}

export function sortOrganizationCourseUpdateEventsNewestFirst (events = []) {
    return [...events].sort(
        (a, b) => getOrganizationCourseUpdateEventTimestamp(b) - getOrganizationCourseUpdateEventTimestamp(a)
    )
}

/**
 * One alert per course: the most recent unviewed update for that course.
 */
export function pickLatestUnviewedEventForCourse (events = [], courseId, normalizeId = normalizeOrganizationCourseId) {
    const courseKey = normalizeId(courseId)
    if (!courseKey) return null
    const forCourse = events.filter(
        (event) => event?.eventId && normalizeId(event.courseId) === courseKey
    )
    return sortOrganizationCourseUpdateEventsNewestFirst(forCourse)[0] || null
}

/**
 * Collapse unviewed events to one entry per course (newest) and track all event ids per course for bulk dismiss.
 */
export function buildUnviewedOrgCourseUpdateMaps (
    events = [],
    { normalizeId = normalizeOrganizationCourseId, dismissedEventIds = {} } = {}
) {
    const byCourse = {}

    events.forEach((event) => {
        if (!event?.courseId || !event?.eventId) return
        if (dismissedEventIds[event.eventId]) return
        const key = normalizeId(event.courseId)
        if (!key) return
        if (!byCourse[key]) byCourse[key] = []
        byCourse[key].push(event)
    })

    const countMap = {}
    const eventMap = {}
    const eventIdsByCourseId = {}

    Object.entries(byCourse).forEach(([key, courseEvents]) => {
        const sorted = sortOrganizationCourseUpdateEventsNewestFirst(courseEvents)
        if (!sorted.length) return
        eventMap[key] = sorted[0]
        eventIdsByCourseId[key] = sorted.map((event) => event.eventId)
        countMap[key] = 1
    })

    return { countMap, eventMap, eventIdsByCourseId }
}

export function formatOrganizationCourseUpdateTimestamp (value) {
    if (value == null || value === '') return '—'
    const n = Number(value)
    const date = Number.isFinite(n) && n > 1e11 ? new Date(n) : new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

export function formatPercentLabel (value) {
    if (value == null) return '—'
    return `${value}%`
}

const STATUS_SORT_ORDER = { updated: 0, skipped: 1, error: 2 }

export function formatOutcomeCountsLine (event) {
    if (!event) return ''
    const parts = []
    const updated = Number(event.learnersUpdatedCount) || 0
    const skipped = Number(event.learnersSkippedCount) || 0
    const errors = Number(event.learnersErrorCount) || 0
    if (updated) parts.push(`${updated} updated`)
    if (skipped) parts.push(`${skipped} skipped`)
    if (errors) parts.push(`${errors} error${errors === 1 ? '' : 's'}`)
    return parts.join(' · ') || 'No learners affected'
}

export function formatOrgVersionLine (event) {
    if (!event) return ''
    const from = event.previousOrgAssignedVersion
    const to = event.newOrgAssignedVersion
    if (!from && !to) return ''
    return `Org version ${from || '—'} → ${to || '—'}`
}

export function sortLearnerOutcomes (outcomes = []) {
    return [...outcomes].sort((a, b) => {
        const sa = STATUS_SORT_ORDER[a.status] ?? 9
        const sb = STATUS_SORT_ORDER[b.status] ?? 9
        if (sa !== sb) return sa - sb
        if (a.status === 'updated') {
            const dropA = (a.previousPercentComplete ?? 0) - (a.newPercentComplete ?? 0)
            const dropB = (b.previousPercentComplete ?? 0) - (b.newPercentComplete ?? 0)
            return dropB - dropA
        }
        return (a.userName || '').localeCompare(b.userName || '')
    })
}

export function extractEventIdFromJobPayload (payload = {}) {
    const id = payload.eventId ?? payload.event_id ?? payload.courseUpdateEventId ?? payload.course_update_event_id
    return id != null && String(id).trim() !== '' ? String(id).trim() : ''
}

export function buildUnviewedOrgCourseUpdateModalEntries ({
    eventMap = {},
    courses = [],
    normalizeId = (id) => String(id ?? '').trim(),
    currentInstructorUserId = '',
} = {}) {
    const instructorId = currentInstructorUserId != null ? String(currentInstructorUserId) : ''
    const courseNameById = {}
    courses.forEach((course) => {
        if (course?.id == null) return
        courseNameById[normalizeId(course.id)] = course.name || 'Course'
    })

    const events = Object.values(eventMap)
        .filter((event) => event?.eventId && event?.courseId)
        .filter((event) => {
            if (!instructorId) return true
            const updaterId = event.updatedByInstructorId != null ? String(event.updatedByInstructorId) : ''
            return !updaterId || updaterId !== instructorId
        })

    return sortOrganizationCourseUpdateEventsNewestFirst(events).map((event) => {
        const courseKey = normalizeId(event.courseId)
        return {
            eventId: event.eventId,
            courseId: event.courseId,
            courseName: event.courseName || courseNameById[courseKey] || 'Course',
            updatedByInstructorName: event.updatedByInstructorName || 'An instructor',
            updatedByInstructorEmail: event.updatedByInstructorEmail || '',
            updatedByInstructorId: event.updatedByInstructorId || '',
            updatedAtLabel: formatOrganizationCourseUpdateTimestamp(event.updatedAt),
            versionLine: formatOrgVersionLine(event),
            outcomeLine: formatOutcomeCountsLine(event),
            rawEvent: event,
        }
    })
}

/**
 * Group unviewed course update entries by the instructor who performed the update (email preferred).
 *
 * @param {ReturnType<typeof buildUnviewedOrgCourseUpdateModalEntries>} entries
 */
export function groupUnviewedOrgCourseUpdateEntriesByInstructor (entries = []) {
    const groupsByKey = {}

    entries.forEach((entry) => {
        if (!entry?.eventId) return
        const email = toTrimmedString(entry.updatedByInstructorEmail).toLowerCase()
        const instructorId = toTrimmedString(entry.updatedByInstructorId)
        const instructorName = toTrimmedString(entry.updatedByInstructorName) || 'An instructor'
        const groupKey = email || instructorId || instructorName.toLowerCase() || entry.eventId

        if (!groupsByKey[groupKey]) {
            groupsByKey[groupKey] = {
                groupKey,
                instructorEmail: email ? entry.updatedByInstructorEmail : '',
                instructorName,
                instructorId,
                entries: [],
            }
        }
        groupsByKey[groupKey].entries.push(entry)
    })

    return Object.values(groupsByKey)
        .map((group) => ({
            ...group,
            entries: [...group.entries].sort(
                (a, b) =>
                    getOrganizationCourseUpdateEventTimestamp(b.rawEvent)
                    - getOrganizationCourseUpdateEventTimestamp(a.rawEvent)
            ),
        }))
        .sort((a, b) => {
            const aTime = getOrganizationCourseUpdateEventTimestamp(a.entries[0]?.rawEvent)
            const bTime = getOrganizationCourseUpdateEventTimestamp(b.entries[0]?.rawEvent)
            return bTime - aTime
        })
}

export function learnerOutcomeStatusLabel (status) {
    if (status === 'skipped') return 'Skipped'
    if (status === 'error') return 'Error'
    return 'Updated'
}

export function formatLearnerProgressCell (learner) {
    if (!learner) return '—'
    if (learner.status === 'skipped') {
        const pct = learner.previousPercentComplete ?? learner.newPercentComplete
        return pct != null ? `${pct}% (no change)` : 'Already current'
    }
    if (learner.status === 'error') {
        return learner.previousPercentComplete != null
            ? `${learner.previousPercentComplete}%`
            : '—'
    }
    const before = formatPercentLabel(learner.previousPercentComplete)
    const after = formatPercentLabel(learner.newPercentComplete)
    return `${before} → ${after}`
}

export function formatLearnerVersionCell (learner) {
    if (!learner) return '—'
    const from = learner.previousCourseVersion
    const to = learner.newCourseVersion
    if (!from && !to) return '—'
    if (from === to) return from || to
    return `${from || '—'} → ${to || '—'}`
}
